import type { ApprovalListTab } from '@fos/shared/contracts/approvals';
import { approvalsWaitingText, approvalYesterdayText } from '@fos/shared/labels';
import { History, Inbox, SearchX, ShieldCheck, X } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Button } from '../../../ui/Button';
import { CountPill } from '../../../ui/CountPill';
import { Page } from '../../../ui/Page';
import { PageHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Segmented } from '../../../ui/Segmented';
import { Select } from '../../../ui/Select';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { MobileFilters, SEGMENTED_TOUCH, useIsPhone } from '../../mobile';
import { ModulePage } from '../../modules/pages/ModulePage';
import { ApprovalCard } from '../components/ApprovalCard';
import { useApprovalCounts, useApprovalDetail } from '../hooks';

const ALL = '__all__';

/**
 * Akceptacje (Figma 03, 3026:2813; mobile 3037:4888): „Czeka · N” / „Historia”, karty rozwijane jedna naraz,
 * stopka „Wczoraj zatwierdzono…”. Parametry adresu: `?id=` rozwija kartę (link z Dziś, Inboxu, banerów),
 * `?klient=` i `?typ=` filtrują, `?widok=historia` otwiera Historię. Decyzje i wykonanie w backendzie
 * (`approvals.*`), UI tylko pokazuje stan (Wykonywana → Wykonana / Błąd z odświeżaniem).
 */
export function ApprovalsPage() {
  const access = useAccess();
  if (!access.can('approvals')) return <ModulePage module="approvals" />;
  return <Approvals />;
}

/** Telefon: karta na liście nie rozwija treści (`hidden md:block`), więc `?id=` prowadzi na ekran szczegółów. */
const MOBILE_QUERY = '(max-width: 767px)';

function useIsMobile(): boolean {
  const get = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_QUERY).matches;
  const [mobile, setMobile] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(MOBILE_QUERY);
    const on = () => setMobile(mq.matches);
    on();
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return mobile;
}

function Approvals() {
  const [params, setParams] = useSearchParams();
  const focusId = params.get('id');
  const mobile = useIsMobile();
  // link do konkretnej akcji (Dziś, Inbox, push, banery, audyt) na telefonie = ekran szczegółów tej akcji
  if (mobile && focusId) return <Navigate to={`/akceptacje/${focusId}`} replace />;
  return <ApprovalsList params={params} setParams={setParams} focusId={focusId} />;
}

function ApprovalsList({
  params,
  setParams,
  focusId,
}: {
  params: URLSearchParams;
  setParams: ReturnType<typeof useSearchParams>[1];
  focusId: string | null;
}) {
  const tab: ApprovalListTab = params.get('widok') === 'historia' ? 'history' : 'pending';
  const clientId = params.get('klient') ?? undefined;
  const type = params.get('typ') ?? undefined;
  const [expanded, setExpanded] = useState<string | null>(focusId);

  useEffect(() => {
    if (focusId) setExpanded(focusId);
  }, [focusId]);

  // Historia stronicowana kursorem („Pokaż wcześniejsze”); „Czeka” to jedna strona
  const list = trpc.approvals.list.useInfiniteQuery(
    { tab, clientId, type, limit: 50 },
    { getNextPageParam: (last) => last.nextCursor ?? undefined },
  );
  const listItems = useMemo(() => list.data?.pages.flatMap((pg) => pg.items) ?? [], [list.data]);
  const counts = useApprovalCounts();
  const stats = trpc.approvals.stats.useQuery(undefined, { staleTime: 60_000 });

  const setParam = (key: string, value: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        if (key !== 'id') next.delete('id');
        return next;
      },
      { replace: true },
    );

  // `?id=` spoza bieżącej listy (np. akcja już w Historii): karta na górze, z podglądem
  const inList = Boolean(focusId && listItems.some((i) => i.id === focusId));
  const focused = useApprovalDetail(focusId, Boolean(list.data) && !inList);

  // „Czeka” = tylko czekające na decyzję (+ „Wymaga ręcznej weryfikacji”): po decyzji karta znika od razu
  // (lista odświeżona po decyzji), wynik wykonania jest w Historii (DECISIONS 2026-10-09)
  const items = useMemo(() => {
    const f = focused.data;
    const head = f && !listItems.some((b) => b.id === f.id) ? [f] : [];
    return [...head, ...listItems];
  }, [listItems, focused.data]);

  const options = useMemo(() => {
    const clients = new Map<string, string>();
    const types = new Map<string, string>();
    for (const i of listItems) {
      if (i.client) clients.set(i.client.id, i.client.name);
      types.set(i.type, i.typeLabel);
    }
    return {
      clients: [...clients]
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pl')),
      types: [...types].map(([value, label]) => ({ value, label })),
    };
  }, [listItems]);

  const onDecided = (id: string) => {
    setExpanded((cur) => (cur === id ? null : cur));
    // karta z adresu `?id=` (spoza listy) też znika po decyzji
    if (focusId === id && tab === 'pending') setParam('id', null);
  };

  const pendingCount = counts.data?.pending ?? null;
  const phone = useIsPhone();
  const clearFilters = () =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('klient');
        next.delete('typ');
        return next;
      },
      { replace: true },
    );
  const filtered = Boolean(clientId || type);
  const footer = stats.data ? approvalYesterdayText(stats.data.yesterday) : null;
  // wyróżnienie (niebieska ramka, główny „Zatwierdź”): akcja z adresu `?id=`, gdy można ją rozstrzygnąć, inaczej pierwsza
  const focusedDecidable = focusId ? items.find((i) => i.id === focusId && i.canDecide)?.id : undefined;
  const firstDecidable = focusedDecidable ?? items.find((i) => i.canDecide)?.id ?? null;

  let content: ReactNode;
  if (list.isPending) {
    content = (
      <div role="status" aria-label="Wczytywanie" className="flex flex-col gap-2.5">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-3 rounded-xl border border-line bg-panel px-4 py-3.5">
            <Skeleton className="size-9 rounded-lg" />
            <div className="flex flex-1 flex-col gap-1.5">
              <Skeleton className="h-3.5 w-1/2" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </div>
        ))}
      </div>
    );
  } else if (list.isError) {
    content = (
      <Section>
        <ErrorState onRetry={() => void list.refetch()} retrying={list.isFetching} />
      </Section>
    );
  } else if (items.length === 0) {
    const empty = filtered ? (
      <EmptyState
        icon={SearchX}
        title="Brak akcji dla tych filtrów"
        action={
          <Button
            variant="secondary"
            onClick={() =>
              setParams(
                (prev) => {
                  const next = new URLSearchParams(prev);
                  next.delete('klient');
                  next.delete('typ');
                  return next;
                },
                { replace: true },
              )
            }
          >
            Wyczyść filtry
          </Button>
        }
      />
    ) : tab === 'pending' ? (
      <EmptyState
        icon={ShieldCheck}
        title="Nic nie czeka na Twoją zgodę"
        description="Gdy AI przygotuje mail, fakturę albo wdrożenie, zobaczysz je tutaj przed wykonaniem."
      />
    ) : (
      <EmptyState
        icon={Inbox}
        title="Historia jest pusta"
        description="Tu trafią zatwierdzone, odrzucone i wygasłe akcje."
      />
    );
    content = <Section>{empty}</Section>;
  } else {
    content = (
      <ul className="flex flex-col gap-2.5">
        {items.map((item) => (
          <li key={item.id}>
            <ApprovalCard
              item={item}
              expanded={expanded === item.id}
              emphasis={tab === 'pending' && item.id === firstDecidable}
              onToggle={() => setExpanded((cur) => (cur === item.id ? null : item.id))}
              onDecided={onDecided}
            />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <Page width="narrow">
      <PageHeader
        title="Akceptacje"
        meta={
          <>
            <span className="hidden md:inline">
              Działania AI, które czekają na Twoją zgodę. Bez niej nic się nie wykona.
            </span>
            <span className="md:hidden">
              {pendingCount === null ? 'Działania AI czekają na Twoją zgodę' : approvalsWaitingText(pendingCount)}
            </span>
          </>
        }
        actions={
          <Segmented
            aria-label="Widok akceptacji"
            className={SEGMENTED_TOUCH}
            value={tab}
            onValueChange={(next) => {
              setExpanded(null);
              setParam('widok', next === 'history' ? 'historia' : null);
            }}
            options={[
              {
                value: 'pending',
                // lifting 17.10.2026: liczba czekających jako pigułka akcentu (CountPill)
                label:
                  pendingCount !== null && pendingCount > 0 ? (
                    <span className="inline-flex items-center gap-1.5">
                      Czeka <CountPill count={pendingCount} tone="accent" />
                    </span>
                  ) : (
                    'Czeka'
                  ),
              },
              { value: 'history', label: 'Historia' },
            ]}
          />
        }
      />

      {/* filtry w Historii (na liście „Czeka” mało informacji naraz, Figma 03); aktywne filtry z adresu zawsze */}
      {(filtered || tab === 'history') && phone && (
        <div className="mt-5 flex justify-end">
          <MobileFilters count={(clientId ? 1 : 0) + (type ? 1 : 0)} onClear={clearFilters} showLabel="Pokaż akcje">
            <Select
              aria-label="Klient"
              value={clientId ?? ALL}
              onValueChange={(v) => setParam('klient', v === ALL ? null : v)}
              options={[
                { value: ALL, label: 'Wszyscy klienci' },
                ...options.clients,
                ...(clientId && !options.clients.some((c) => c.value === clientId)
                  ? [{ value: clientId, label: 'Wybrany klient' }]
                  : []),
              ]}
            />
            <Select
              aria-label="Typ akcji"
              value={type ?? ALL}
              onValueChange={(v) => setParam('typ', v === ALL ? null : v)}
              options={[
                { value: ALL, label: 'Wszystkie typy' },
                ...options.types,
                ...(type && !options.types.some((t) => t.value === type)
                  ? [{ value: type, label: 'Wybrany typ' }]
                  : []),
              ]}
            />
          </MobileFilters>
        </div>
      )}
      {(filtered || tab === 'history') && !phone && (
        <div className="mt-6 flex flex-wrap items-center gap-2">
          <Select
            size="s"
            aria-label="Klient"
            className="w-auto min-w-[160px]"
            value={clientId ?? ALL}
            onValueChange={(v) => setParam('klient', v === ALL ? null : v)}
            options={[
              { value: ALL, label: 'Wszyscy klienci' },
              ...options.clients,
              ...(clientId && !options.clients.some((c) => c.value === clientId)
                ? [{ value: clientId, label: 'Wybrany klient' }]
                : []),
            ]}
          />
          <Select
            size="s"
            aria-label="Typ akcji"
            className="w-auto min-w-[140px]"
            value={type ?? ALL}
            onValueChange={(v) => setParam('typ', v === ALL ? null : v)}
            options={[
              { value: ALL, label: 'Wszystkie typy' },
              ...options.types,
              ...(type && !options.types.some((t) => t.value === type) ? [{ value: type, label: 'Wybrany typ' }] : []),
            ]}
          />
          {filtered && (
            <Button
              variant="ghost"
              size="s"
              icon={X}
              onClick={() =>
                setParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete('klient');
                    next.delete('typ');
                    return next;
                  },
                  { replace: true },
                )
              }
            >
              Wyczyść
            </Button>
          )}
        </div>
      )}

      <div className="mt-6 md:mt-8">{content}</div>
      {list.hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button variant="secondary" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
            Pokaż wcześniejsze
          </Button>
        </div>
      )}

      {tab === 'pending' && footer && (
        <p className="mt-6 flex items-center gap-2 text-caption text-fg-muted">
          <History size={14} strokeWidth={1.75} aria-hidden className="shrink-0" />
          <span>
            {footer}{' '}
            <Link
              to="/akceptacje?widok=historia"
              className="rounded-xs font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
            >
              Historia
            </Link>
          </span>
        </p>
      )}
    </Page>
  );
}
